import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Download, Calendar, Loader2, Mail, SlidersHorizontal, MessageCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { shareHint } from '../../utils/shareInfo';
import EmailDocDialog from './EmailDocDialog';
import { SettlementWorking } from './SettlementWorking';
import { SignoffBadge } from './SignoffBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BASIS_OPTIONS = [
  { value: 'basic', label: 'Daily Basic Wage', hint: 'Basic ÷ 30 — UAE statutory default' },
  { value: 'basic_housing', label: 'Basic + Housing', hint: '(Basic + Housing) ÷ 30' },
  { value: 'full', label: 'Full Salary', hint: '(Basic + Housing + Transport + Other) ÷ 30' },
];

// ─── Final Leave Salary Settlement (for resigned/terminated employees) ───
export const LeaveSettlementDialog = ({ open, onClose, employee }) => {
  const [lwd, setLwd] = useState('');
  const [basis, setBasis] = useState('basic');
  const [showBasis, setShowBasis] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);

  useEffect(() => {
    if (!open || !employee) return;
    setErr(null);
    setBasis('basic');
    setShowBasis(false);
    const defaultLwd = employee.last_working_date
      ? employee.last_working_date.split('T')[0]
      : new Date().toISOString().split('T')[0];
    setLwd(defaultLwd);
  }, [open, employee]);

  const load = async (useLwd, useBasis = basis) => {
    if (!employee) return;
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams();
      if (useLwd) params.set('lwd', useLwd);
      params.set('basis', useBasis);
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/offboarding/leave-settlement/${employee.id}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Failed to compute');
      }
      setData(await r.json());
    } catch (e) {
      setErr(e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && employee && lwd) load(lwd, basis); /* eslint-disable-next-line */ }, [open, employee, lwd, basis]);

  const download = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(
        `${API_URL}/api/offboarding/leave-settlement/${employee.id}/download?format=${format}&lwd=${lwd}&basis=${basis}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      a.download = `LeaveSettlement_${(employee.name || 'Employee').replace(/\s+/g, '_')}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast({ title: 'Download failed', description: e.message, variant: 'destructive' });
    }
  };

  if (!employee) return null;

  const basisLabel = (BASIS_OPTIONS.find(o => o.value === basis) || BASIS_OPTIONS[0]).label;

  const shareWhatsApp = async () => {
    try {
      const params = new URLSearchParams();
      if (lwd) params.set('lwd', lwd);
      params.set('basis', basis);
      params.set('origin', window.location.origin);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/offboarding/leave-settlement/${employee.id}/share-link?${params}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const { token: tok, mobile, name, amount, share_count, last_shared_at } = await res.json();
      setShareInfo({ share_count, last_shared_at });
      const url = `${window.location.origin}/settlement-confirm/${tok}`;
      const msg = `Hi ${name || ''}, here is your Final Leave Salary settlement.\n`
        + `Amount Payable: AED ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.\n`
        + `Please review and confirm (or raise a query): ${url}`;
      const digits = String(mobile || '').replace(/[^0-9]/g, '');
      if (!digits) toast({ title: 'No mobile on file', description: 'Opening WhatsApp without a number — pick the contact manually.' });
      window.open(digits ? `https://wa.me/${digits}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    } catch {
      toast({ title: 'Could not create WhatsApp link', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="leave-settlement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-violet-600" />
            Final Leave Salary Settlement — {employee.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-violet-50 rounded p-3 text-xs space-y-0.5">
            <p><span className="text-gray-500">Employee Code:</span> <b>{employee.employee_code}</b></p>
            <p><span className="text-gray-500">Status:</span> <b>{employee.employment_status || 'Active'}</b></p>
            <p className="text-gray-500 italic">Based on {basisLabel} · AED daily rate = {basisLabel} / 30</p>
          </div>

          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Last Working Day (LWD)</Label>
              <Input
                type="date"
                value={lwd}
                onChange={e => setLwd(e.target.value)}
                data-testid="settlement-lwd"
              />
            </div>
            <Button variant="outline" onClick={() => load(lwd, basis)} disabled={loading} data-testid="settlement-recalc">
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Recalculate
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowBasis(v => !v)}
              className="text-gray-500 hover:text-violet-700"
              data-testid="settlement-basis-toggle"
            >
              <SlidersHorizontal className="h-4 w-4 mr-1" /> Calculation basis
            </Button>
          </div>

          {showBasis && (
            <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3 space-y-2" data-testid="settlement-basis-panel">
              <p className="text-[11px] text-gray-500">
                Default is <b>Daily Basic Wage</b> (UAE statutory). Switch only for special / management-approved cases.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {BASIS_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setBasis(opt.value)}
                    className={`text-left rounded-md border px-3 py-2 transition-colors ${
                      basis === opt.value
                        ? 'border-violet-500 bg-white ring-1 ring-violet-300'
                        : 'border-gray-200 bg-white hover:border-violet-300'
                    }`}
                    data-testid={`settlement-basis-${opt.value}`}
                  >
                    <p className="text-xs font-semibold text-gray-800">{opt.label}</p>
                    <p className="text-[10px] text-gray-500">{opt.hint}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {err && <div className="text-xs text-rose-600 bg-rose-50 rounded p-2">{err}</div>}

          <SignoffBadge employeeId={employee.id} open={open} lwd={data?.last_working_day} amount={data?.net_settlement ?? data?.leave_salary} />

          {data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                <div className="bg-gray-50 rounded p-3">
                  <p className="text-[10px] uppercase text-gray-500">Service</p>
                  <p className="font-bold text-gray-800">{data.service_years}y {data.service_months}m {data.service_days}d</p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <p className="text-[10px] uppercase text-gray-500">Eligible</p>
                  <p className="font-bold text-gray-800">{data.total_eligible_days}</p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <p className="text-[10px] uppercase text-gray-500">Taken</p>
                  <p className="font-bold text-gray-800">{data.already_taken_days}</p>
                </div>
                <div className="bg-amber-50 rounded p-3" title="Accrued leave beyond the 15-day carry-forward cap that was not used in time">
                  <p className="text-[10px] uppercase text-amber-700">C/F Lapsed</p>
                  <p className="font-bold text-amber-800">{data.carry_forward_lapsed_days || 0}</p>
                </div>
                <div className="bg-violet-50 rounded p-3">
                  <p className="text-[10px] uppercase text-violet-700">Pending Balance</p>
                  <p className="font-bold text-violet-800">{data.balance_days} days</p>
                </div>
              </div>

              <p className="text-[11px] text-gray-500 italic">
                Formula: Pending Balance = Eligible ({data.total_eligible_days}) − Taken ({data.already_taken_days}) − C/F Lapsed ({data.carry_forward_lapsed_days || 0}) = <b>{data.balance_days}</b> days
              </p>

              <SettlementWorking data={data} />

              <p className="text-[11px] text-gray-500" data-testid="settlement-salary-breakdown">
                Daily rate = {data.basis_label || basisLabel} {data.currency} {(data.monthly_wage ?? data.basic_salary)?.toLocaleString()} ÷ 30 = {data.currency} {data.daily_rate.toLocaleString()}/day
                <span className="text-gray-400"> &nbsp;(Basic {data.basic_salary?.toLocaleString()} + Housing {(data.housing||0).toLocaleString()} + Transport {(data.transportation||0).toLocaleString()} + Other {(data.other||0).toLocaleString()})</span>
              </p>

              <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase opacity-90">Total Leave Salary Payable</p>
                  <p className="text-[10px] opacity-70">{data.balance_days} days × {data.currency} {data.daily_rate.toLocaleString()}/day</p>
                </div>
                <p className="text-3xl font-bold" data-testid="settlement-total">
                  {data.currency} {data.leave_salary.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            variant="outline"
            onClick={() => setEmailOpen(true)}
            disabled={!data}
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            data-testid="settlement-email-staff-btn"
          >
            <Mail className="h-4 w-4 mr-1" /> Email to Staff
          </Button>
          <Button
            variant="outline"
            onClick={shareWhatsApp}
            disabled={!data}
            className="border-green-200 text-green-700 hover:bg-green-50"
            data-testid="settlement-whatsapp-btn"
          >
            <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
          </Button>
          {shareInfo?.share_count > 0 && (
            <span className="w-full order-last text-right text-[11px] text-gray-400" data-testid="settlement-share-hint">
              {shareHint(shareInfo)} · link valid 30 days
            </span>
          )}
          <Button
            variant="outline"
            onClick={() => download('pdf')}
            disabled={!data}
            className="border-violet-300 text-violet-700 hover:bg-violet-50"
            data-testid="settlement-download-pdf"
          >
            <Download className="h-4 w-4 mr-1" /> Download PDF
          </Button>
          <Button
            onClick={() => download('excel')}
            disabled={!data}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="settlement-download-excel"
          >
            <Download className="h-4 w-4 mr-1" /> Download Excel
          </Button>
        </DialogFooter>
      </DialogContent>
      <EmailDocDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        title="Email Final Leave Salary Settlement"
        recipientName={employee?.name}
        attachmentLabel="leave settlement statement"
        payload={{ doc_type: 'leave_settlement', employee_id: employee?.id, lwd, leave_settlement_basis: basis, app_origin: window.location.origin }}
      />
    </Dialog>
  );
};

export default LeaveSettlementDialog;
